import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from './usePermissions';
import { toast } from 'sonner';

export type MaintenanceWorkType = 'maintenance' | 'repair' | 'inspection' | 'calibration' | 'warranty';

export interface MaintenancePlan {
  id: string;
  shop_id: string;
  asset_id: string;
  name: string;
  description: string | null;
  interval_days: number;
  next_due_at: string;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface MaintenanceLog {
  id: string;
  shop_id: string;
  asset_id: string;
  plan_id: string | null;
  performed_at: string;
  performed_by_user_id: string | null;
  work_type: MaintenanceWorkType;
  notes: string | null;
  created_at: string;
  // Joined
  performer?: { id: string; display_name: string } | null;
  plan?: { id: string; name: string } | null;
}

// Maintenance Plans
export function useMaintenancePlans(assetId: string | null) {
  const { activeShop } = useAuth();
  const shopId = activeShop?.id;

  return useQuery({
    queryKey: ['maintenance-plans', assetId],
    queryFn: async () => {
      if (!assetId || !shopId) return [];
      
      const { data, error } = await supabase
        .from('shop_asset_maintenance_plans')
        .select('*')
        .eq('asset_id', assetId)
        .eq('shop_id', shopId)
        .order('next_due_at');
      
      if (error) throw error;
      return data as MaintenancePlan[];
    },
    enabled: !!assetId && !!shopId,
  });
}

export function useCreateMaintenancePlan() {
  const { activeShop, user } = useAuth();
  const { canEditAssets } = usePermissions();
  const queryClient = useQueryClient();
  const shopId = activeShop?.id;

  return useMutation({
    mutationFn: async (input: {
      asset_id: string;
      name: string;
      description?: string;
      interval_days: number;
      next_due_at: string;
    }) => {
      if (!shopId) throw new Error('No active shop');
      if (!canEditAssets) throw new Error('You do not have permission to manage maintenance plans');

      const { data, error } = await supabase
        .from('shop_asset_maintenance_plans')
        .insert({
          shop_id: shopId,
          created_by: user?.id,
          ...input,
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-plans', data.asset_id] });
      toast.success('Maintenance plan created');
    },
    onError: (error) => {
      toast.error('Failed to create plan', { description: error.message });
    },
  });
}

// Maintenance Logs
export function useMaintenanceLogs(assetId: string | null) {
  const { activeShop } = useAuth();
  const shopId = activeShop?.id;

  return useQuery({
    queryKey: ['maintenance-logs', assetId],
    queryFn: async () => {
      if (!assetId || !shopId) return [];
      
      const { data, error } = await supabase
        .from('shop_asset_maintenance_logs')
        .select(`
          *,
          performer:profiles!performed_by_user_id(id, display_name),
          plan:shop_asset_maintenance_plans(id, name)
        `)
        .eq('asset_id', assetId)
        .eq('shop_id', shopId)
        .order('performed_at', { ascending: false });
      
      if (error) throw error;
      return data as MaintenanceLog[];
    },
    enabled: !!assetId && !!shopId,
  });
}

export function useCreateMaintenanceLog() {
  const { activeShop, user } = useAuth();
  const { canEditAssets } = usePermissions();
  const queryClient = useQueryClient();
  const shopId = activeShop?.id;

  return useMutation({
    mutationFn: async (input: {
      asset_id: string;
      plan_id?: string;
      work_type: MaintenanceWorkType;
      notes?: string;
      performed_at?: string;
    }) => {
      if (!shopId) throw new Error('No active shop');
      if (!canEditAssets) throw new Error('You do not have permission to log maintenance');

      const { data, error } = await supabase
        .from('shop_asset_maintenance_logs')
        .insert({
          shop_id: shopId,
          performed_by_user_id: user?.id,
          performed_at: input.performed_at || new Date().toISOString(),
          ...input,
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-logs', data.asset_id] });
      toast.success('Maintenance logged');
    },
    onError: (error) => {
      toast.error('Failed to log maintenance', { description: error.message });
    },
  });
}

// Work type labels
export const WORK_TYPE_LABELS: Record<MaintenanceWorkType, string> = {
  maintenance: 'Maintenance',
  repair: 'Repair',
  inspection: 'Inspection',
  calibration: 'Calibration',
  warranty: 'Warranty Work',
};
